import _ from 'lodash'
import type { BeckyFunctionLibrary, BeckyModel, BeckyRequestPayload } from '@wix/thunderbolt-becky-root'
import type { Data, Logger, NicknameToCompIdMaps, RefSchemas, Structure } from '@wix/thunderbolt-becky-types'
import {
	createRemoteRefsFetcher,
	resolveCommonAspects as byrefResolveCommonAspects,
	fixBuilderWidgetComp,
} from '@wix/thunderbolt-byref'

const byref = {
	createRemoteRefsFetcher,
	resolveCommonAspects: byrefResolveCommonAspects,
	fixBuilderWidgetComp,
}

export const resolveCommonAspects = (
	beckyModel: BeckyModel,
	masterPageStructure: Structure,
	masterPageData: Data,
	functionLibrary: BeckyFunctionLibrary,
	requestPayload: BeckyRequestPayload,
	schemas: RefSchemas,
	logger?: Logger
) =>
	functionLibrary.runAsyncAndReport(async () => {
		const resolveGhosts = requestPayload.resolveGhosts === 'true'
		const beckyTopology = { publicTopology: beckyModel.publicTopology, environmentTopology: beckyModel.environmentTopology }

		const fetchRemoteRefs = functionLibrary.runAndReport(
			() =>
				byref.createRemoteRefsFetcher({
					fetchUrl: functionLibrary.fetchUrl.bind(functionLibrary),
					clientSpecMap: beckyModel.clientSpecMap,
					beckyTopology,
					requestPayload,
					isMigrateStylableComponentsInBlocksWidgetsExpOn: Boolean(
						beckyModel.experiments['specs.thunderbolt.migrateStylableComponentsInBlocksWidgets']
					),
					logger,
					shouldFetchNicknamesMap: Boolean(
						beckyModel.experiments['specs.thunderbolt.renderPlatformBuilderComponent']
					),
				}),
			'createRemoteRefsFetcher'
		)

		const builderCompIdToDataQuery: Record<string, string> = {}
		const builderWidgetIdToCompId: Record<string, string> = {}

		for (const compId in beckyModel.structure) {
			const comp = beckyModel.structure[compId]
			if (beckyModel.experiments['specs.thunderbolt.renderPlatformBuilderComponent'] && comp.builderType) {
				if (comp.dataQuery) {
					builderCompIdToDataQuery[compId] = comp.dataQuery
				}
				const { fixedComp, compDataItem } = byref.fixBuilderWidgetComp(comp)
				beckyModel.structure[compId] = fixedComp
				beckyModel.data.document_data[compDataItem.id] = compDataItem
				builderWidgetIdToCompId[compDataItem.widgetId] = compId
			}
		}

		const model = {
			...beckyModel,
			...(beckyModel.pageCompId === 'masterPage' && { structure: masterPageStructure, data: masterPageData }),
			masterPageStructure,
			masterPageData,
			requestPayload,
			resolveGhosts,
		}

		const widgetTemplatesStore = await functionLibrary.runAsyncAndReport(
			() => fetchRemoteRefs(model.data.document_data),
			'fetchRemoteRefs'
		)

		const viewMode = requestPayload.viewMode!
		const nicknameToCompIdMaps = Object.values(widgetTemplatesStore[viewMode] || []).reduce(
			(map, refTemplate) => Object.assign(map, refTemplate.nicknameToCompIdMap),
			{} as NicknameToCompIdMaps
		)

		const compIdToNicknameMaps = _(nicknameToCompIdMaps)
			.mapKeys((__, widgetId) => builderWidgetIdToCompId[widgetId])
			.mapValues((nicknames) => _.invert(nicknames))
			.value()

		const res = functionLibrary.runAndReport(
			() =>
				byref.resolveCommonAspects(
					{
						...model,
						widgetTemplatesStore,
						builderCompIdToDataQuery,
						compIdToNicknameMaps,
					},
					schemas,
					functionLibrary.runAndReport
				),
			'resolveCommonAspects'
		)

		return { ...model, ...res, nicknameToCompIdMaps }
	}, 'tb-byRef-resolving')
